import * as React from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import _ from "lodash";
import { formatCurrency, formatDate } from "../../helpers/UtilityHelper";
import { Panel, Donut, Lollipop, StackedComposition, AreaCurve } from "../dashboard/NewCharts";
import DelayMisDownloadButton from "./DelayMisDownloadButton";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import OrderItemsModal from "../../common/OrderItemsModal";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { apiConstants, post } from "../../common/constants";
import { FONT, WEIGHT } from "../../styles/foundation/typography";
import { BORDER, SUBTLE, WHITE } from "../../styles/foundation/colors";
import { cellStackSx, primaryTextSx, secondaryTextSx, tertiaryTextSx, awbTextSx } from "../orders/list2/cellStyles";
import { delayChip, routeCell } from "./ctCells";
import AwbLinkCell from "../../common/AwbLinkCell";
import useAddressesByAwb from "../../common/hooks/useAddressesByAwb";
import { SHADOW } from "../../styles/foundation/effects";

const RTOR = "#c0392b";
// Distinct slice colours for the "by current status" donut (a categorical split, not ordered).
const STATUS_COLORS = ["#c0392b", "#e58200", "#d99a3a", "#7a4fb0", "#3783e7", "#2e9e6b", "#0f766e", "#8f2f2c"];
const AGE_ORDER = ["0-15", "16-30", "31-45", "46-60", "61-75", "76-100", "100 +"];
const AGE_COLORS = ["#0a9d72", "#c79000", "#e58200", "#dd5a2a", "#d24b47", "#c0392b", "#8f2f2c"];
const rtoDelayKeys = ["0-15 Days", "16-30 Days", "31-45 Days", "46-60 Days", "61-75 Days", "76-100 Days", "100+ Days"];
const RTO_BUCKETS = rtoDelayKeys.map((k, i) => ({ key: k, label: k.replace(" Days", ""), color: AGE_COLORS[i] }));

export default function RtoDelay({ rtoDelayed }) {
  const [sizePerPage, setSizePerPage] = React.useState(20);
  const [page, setPage] = React.useState(0);
  const [orderItems, setOrderItems] = React.useState(null);
  const [openList, setOpenList] = React.useState(false);

  // Current page's rows — enrich only these AWBs with pickup/drop addresses for the grid.
  const pageData = _.orderBy(rtoDelayed, "rtoDeliveryBreachedAgeingDays", "desc").splice(page * sizePerPage, sizePerPage);
  const { addrMap } = useAddressesByAwb(pageData.map((r) => r.awbNumber));

  const statusChartData = _(rtoDelayed)
    .groupBy(function (item) {
      return item?.status?.replaceAll("_", " ");
    })
    .map(function (items, key) {
      return { name: key, value: items.length };
    })
    .orderBy("value", "desc")
    .value()
    .filter((d) => d.name && d.name !== "undefined");

  // Colour each status slice for the donut; centre shows the total delayed RTOs.
  const statusDonut = statusChartData.map((d, i) => ({ ...d, color: STATUS_COLORS[i % STATUS_COLORS.length] }));
  const statusTotal = _.sumBy(statusChartData, "value");

  const delayedAgingChartData = _(rtoDelayed)
    .groupBy(function (item) {
      if (item.rtoDeliveryBreachedAgeingDays >= 101) return "100 +";
      if (item.rtoDeliveryBreachedAgeingDays >= 0 && item.rtoDeliveryBreachedAgeingDays <= 15) return "0-15";
      if (item.rtoDeliveryBreachedAgeingDays >= 16 && item.rtoDeliveryBreachedAgeingDays <= 30) return "16-30";
      if (item.rtoDeliveryBreachedAgeingDays >= 31 && item.rtoDeliveryBreachedAgeingDays <= 45) return "31-45";
      if (item.rtoDeliveryBreachedAgeingDays >= 46 && item.rtoDeliveryBreachedAgeingDays <= 60) return "46-60";
      if (item.rtoDeliveryBreachedAgeingDays >= 61 && item.rtoDeliveryBreachedAgeingDays <= 75) return "61-75";
      if (item.rtoDeliveryBreachedAgeingDays >= 76 && item.rtoDeliveryBreachedAgeingDays <= 100) return "76-100";
    })
    .map(function (items, key) {
      return { name: key, value: items.length };
    })
    .value()
    .filter((d) => d.name && d.name !== "undefined");

  const agingData = _.orderBy(delayedAgingChartData, (d) => AGE_ORDER.indexOf(d.name))
    .map((d) => ({ ...d, color: AGE_COLORS[AGE_ORDER.indexOf(d.name)] || RTOR }));

  const courierWiseSplit = _(rtoDelayed)
    .groupBy("courierName")
    .map(function (items, key) {
      return {
        name: key.replaceAll('Forward', '').replaceAll('_', ' ').replaceAll('FORWARD', '').replaceAll('-', ''),
        value: items.length,
      };
    })
    .orderBy("value", "desc")
    .value();

  const rtoDelayGroupByCourier = _(rtoDelayed)
    .groupBy("courierName")
    .map(function (items, key) {
      const grouped = _(items).groupBy("rtoDelayLabel").value();
      let values = {};
      for (let k of Object.keys(grouped)) values[k] = grouped[k].length;
      return { name: key, values };
    })
    .orderBy((r) => _.sum(Object.values(r.values)), "desc")
    .value();

  const getItemList = (awb) => {
    const bodyData = { includes: "itemList", andfilter: [{ field: "awbNumber", operator: "eq", value: awb }] };
    post(`${apiConstants.BASE_LOCAL}${apiConstants.ORDER_LISTS}`, bodyData)
      .then((response) => {
        setOpenList(true);
        setOrderItems(response.data.data[0].item_list);
      })
      .catch((err) => {
        setOpenList(false);
        console.error(err);
      });
  };

  const handleClose = () => {
    setOrderItems(null);
    setOpenList(false);
  };

  return (
    <Grid container spacing={2}>
      <OrderItemsModal items={orderItems} setOpen={handleClose} openList={openList} />

      <Grid item xs={12}>
        <Panel title="RTO Delivery Delay Ageing" sub="Delayed RTO shipments by days pending (RTO request date + 15 days) — the curve traces the falloff so you see where most of the RTO backlog sits.">
          <AreaCurve data={agingData} height={300} color={RTOR} xLabel="Days pending (RTO req + 15d)" yLabel="Shipments" valueName="RTO shipments" />
        </Panel>
      </Grid>

      <Grid item xs={12} md={5}>
        <Panel title="Delayed RTO Shipments — by Current Status" sub="RTO shipments not delivered back to origin 15+ days after the RTO request.">
          <Donut data={statusDonut} centerValue={statusTotal.toLocaleString()} centerLabel="RTO alerts" />
        </Panel>
      </Grid>
      <Grid item xs={12} md={7}>
        <Panel title="Courier Partners for RTO Delivery Delays">
          <Lollipop data={courierWiseSplit} nameLabel="Courier partner" valueLabel="Delayed RTOs" />
        </Panel>
      </Grid>

      <Grid item xs={12}>
        <Panel title="Courier-wise RTO Delay" sub="Split of each courier's delayed RTOs across the ageing buckets.">
          <StackedComposition rows={rtoDelayGroupByCourier} buckets={RTO_BUCKETS} />
        </Panel>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: "10px", boxShadow: SHADOW.card, overflow: "hidden" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontFamily: FONT, fontSize: "11px", fontWeight: WEIGHT.extra, letterSpacing: "0.07em", textTransform: "uppercase", color: SUBTLE }}>RTO Delayed Shipments</Typography>
            <DelayMisDownloadButton
              rows={rtoDelayed}
              filename="RTO Delayed Shipments"
              baseColumns={[
                { label: "Reference", value: "shippingLabelId" },
                { label: "Courier Partner", value: "courierName" },
                { label: "AWB Number", value: "awbNumber" },
                { label: "Order Date", value: (row) => formatDate(row.awbRegisteredDate, "dd MMM, yyyy") },
                { label: "Zone", value: "merchantZone" },
                { label: "Delay", value: "rtoDeliveryBreachedAgeingDays" },
              ]}
            />
          </Box>
          <Box sx={{ px: 0 }}>
            <ProzoDataGrid
              columns={[
                {
                  field: "order", headerName: "Order Details", width: 190, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => (
                    <Box sx={cellStackSx}>
                      <Typography sx={primaryTextSx}>{row.reference || row.shippingLabelId || "—"}</Typography>
                      {row.shippingLabelId && row.shippingLabelId !== row.reference && <Typography sx={secondaryTextSx}>{row.shippingLabelId}</Typography>}
                      <Typography sx={secondaryTextSx}>{formatDate(row.awbRegisteredDate, "dd MMM, yyyy")}</Typography>
                      <Button sx={{ p: 0, minWidth: 0, justifyContent: "flex-start", fontSize: 11, textTransform: "none" }}
                        startIcon={<VisibilityIcon sx={{ fontSize: "14px !important" }} />} variant="link"
                        onClick={() => getItemList(row.awbNumber)}>View Items</Button>
                    </Box>
                  ),
                },
                {
                  field: "courier", headerName: "Courier Partner", width: 210, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => (
                    <Box sx={cellStackSx}>
                      <AwbLinkCell awb={row.awbNumber} sx={awbTextSx} />
                      <Typography sx={secondaryTextSx}>{addrMap[row.awbNumber]?.courierLabel || row.courierName}</Typography>
                    </Box>
                  ),
                },
                {
                  field: "route", headerName: "Pickup → Drop", width: 250, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => routeCell(row, addrMap),
                },
                {
                  field: "timeline", headerName: "Timeline", width: 210, headerAlign: "left", hideSortIcons: true,
                  renderCell: ({ row }) => (
                    <Box sx={cellStackSx}>
                      <Typography sx={secondaryTextSx}><Box component="span" sx={{ ...primaryTextSx, fontSize: "inherit" }}>RTO req: </Box>{row.rtoRequestedTimestamp ? formatDate(row.rtoRequestedTimestamp, "dd MMM, hh:mm aa") : "—"}</Typography>
                      <Typography sx={secondaryTextSx}><Box component="span" sx={{ ...primaryTextSx, fontSize: "inherit" }}>Last update: </Box>{row.lastStatusUpdateTime ? formatDate(row.lastStatusUpdateTime, "dd MMM, hh:mm aa") : "—"}</Typography>
                    </Box>
                  ),
                },
                {
                  field: "delay", headerName: "RTO Delay", width: 140, headerAlign: "center", align: "center", hideSortIcons: true,
                  renderCell: ({ row }) => delayChip(row.rtoDeliveryBreachedAgeingDays, { amber: 16, red: 31 }),
                },
                {
                  field: "invoice", headerName: "Invoice", width: 130, headerAlign: "right", align: "right", hideSortIcons: true,
                  renderCell: ({ row }) => <Typography sx={primaryTextSx}>{formatCurrency(Math.round(row.invoiceValue || 0))}</Typography>,
                },
              ]}
              autoHeight={true}
              rows={pageData}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              setPage={setPage}
              page={page}
              pagination={true}
              hideFooterPagination={false}
              filterMode={"server"}
              rowCount={rtoDelayed.length}
              getRowId={(row) => row.id}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
